import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { toUserError } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { RetryButton } from "./RetryButton";
import { useRecoveryAction } from "./use-recovery-action";

export interface ErrorStateProps {
  error: unknown;
  /** Wired to the mapped action when it's `"retry"` — omit and the button
   * falls back to a full reload (see `use-recovery-action.ts`). */
  onRetry?: () => void;
  /** Per-view context folded into the title only, e.g. "reader" ->
   * "The reader view hit a snag" — the message/action still come from
   * `toUserError`. Used by `AppErrorBoundary`. */
  label?: string;
  /** Shows a spinner on the recovery button while a retry is in flight. */
  pending?: boolean;
  className?: string;
}

/** "Something went wrong" — renders {@link toUserError}'s curated copy plus
 * its recovery action. This is the ONLY place in the studio that should turn
 * a caught error into UI; everything else composes this component (or
 * `notifyError` for a toast) instead of writing its own failure copy. */
export function ErrorState({ error, onRetry, label, pending = false, className }: ErrorStateProps) {
  const userError = toUserError(error);
  const { resolve } = useRecoveryAction();
  const handler = resolve(userError.action, onRetry);
  const title = label ? `The ${label} view hit a snag` : userError.title;

  return (
    <div role="alert" className={cn("flex flex-col items-center gap-2.5 p-6 text-center", className)}>
      <div className="grid h-10 w-10 place-items-center rounded-full bg-destructive/15 text-destructive">
        <AlertTriangle className="h-5 w-5" aria-hidden />
      </div>
      <p className="font-display text-[15px] text-foreground">{title}</p>
      <p className="max-w-sm text-[13px] leading-relaxed text-muted-foreground">{userError.message}</p>
      {userError.action && handler && (
        <div className="mt-1">
          {userError.action.kind === "retry" ? (
            <RetryButton onRetry={handler} pending={pending} label={userError.action.label} />
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={handler}>
              {userError.action.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
